import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginForm } from './shared/LoginForm';
import { RegisterForm } from './shared/RegisterForm';
import { ForgotPasswordForm } from './shared/ForgotPasswordForm';
import { Track, CreateFeedbackRequest, CreateTimestampedNoteRequest, CreateArtistRequest, Artist } from '../types/artist';
import { trackApi } from '../services/trackApi';
import { feedbackApi } from '../services/feedbackApi';
import { artistApi } from '../services/artistApi';
import { API_BASE_URL } from '../config/api';
import analytics from '../services/analytics';
import { usePageTracking } from '../hooks/usePageTracking';
import { ArtistOverview } from './shared/ArtistOverview';
import { TrackCard } from './shared/TrackCard';
import { TransportControl } from './shared/TransportControl';
import { FeedbackForm } from './shared/FeedbackForm';
import { ArtistSetupCard } from './shared/ArtistSetupCard';
import { 
  FeedbackPageLayout, 
  FeedbackSection, 
  FeedbackMessage, 
  FeedbackModal 
} from './shared/FeedbackPageLayout';
import './RequestFeedback.css';

const RequestFeedback: React.FC = () => {
  const { publicTrackID } = useParams<{ publicTrackID: string }>();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  
  // Track page view
  usePageTracking('Request Feedback');
  
  const [track, setTrack] = useState<Track | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState<string | null>(null);
  
  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authView, setAuthView] = useState<'login' | 'register' | 'forgot-password'>('login');
  
  // Artist state
  const [checkingArtists, setCheckingArtists] = useState(true);
  const [showArtistForm, setShowArtistForm] = useState(false);
  const [artistName, setArtistName] = useState('');
  const [artistUsesAI, setArtistUsesAI] = useState(false);
  const [creatingArtist, setCreatingArtist] = useState(false);
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [fullArtist, setFullArtist] = useState<Artist | null>(null);
  
  // Feedback form state
  const [overallRating, setOverallRating] = useState(5);
  const [compositionRating, setCompositionRating] = useState(5);
  const [musicQualityRating, setMusicQualityRating] = useState(5);
  const [productionQualityRating, setProductionQualityRating] = useState(5);
  const [review, setReview] = useState('');
  const [timestampedNotes, setTimestampedNotes] = useState<CreateTimestampedNoteRequest[]>([]);
  const [currentNote, setCurrentNote] = useState('');
  
  const audioRef = useRef<HTMLAudioElement>(null);

  const checkUserArtists = useCallback(async () => {
    if (!user?.id) {
      setCheckingArtists(false);
      return;
    }

    try {
      setCheckingArtists(true);
      const userArtists = await artistApi.getArtistsByUserId(user.id);
      
      // If user has no artists, show the artist creation form
      if (userArtists.length === 0) {
        setShowArtistForm(true);
      }
    } catch (err) {
      console.error('Failed to load user artists:', err);
      // If we can't load artists, assume none exist and show form
      setShowArtistForm(true);
    } finally {
      setCheckingArtists(false);
    }
  }, [user?.id]);

  const createArtist = async () => {
    if (!user?.id || !artistName.trim()) return;

    try {
      setCreatingArtist(true);
      const createRequest: CreateArtistRequest = {
        name: artistName.trim(),
        usesAI: artistUsesAI,
      };
      
      await artistApi.createArtist(createRequest);
      setShowArtistForm(false);
      setArtistName('');
      setArtistUsesAI(false);
    } catch (err) {
      console.error('Failed to create artist:', err);
      setError('Failed to create artist profile. Please try again.');
    } finally {
      setCreatingArtist(false);
    }
  };

  const loadTrack = useCallback(async () => {
    if (!publicTrackID) {
      setError('No track ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const trackData = await trackApi.getTrackByPublicId(publicTrackID);
      setTrack(trackData);
    } catch (err) {
      console.error('Failed to load track:', err);
      
      // Provide more user-friendly error messages
      if (err instanceof Error) {
        if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          setError('You need to be logged in to access this track. Please log in and try again.');
        } else if (err.message.includes('404') || err.message.includes('Not Found')) {
          setError('Track not found. Please check the URL and try again.');
        } else if (err.message.includes('Invalid response format')) {
          setError('Unable to load track information. Please try again later.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to load track. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [publicTrackID]);

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        setShowAuthModal(true);
        setLoading(false); // Don't show loading for unauthenticated users
      } else {
        setShowAuthModal(false);
        // Check for user's artists first, then load track
        checkUserArtists();
        loadTrack();
      }
    }
  }, [authLoading, isAuthenticated, loadTrack, checkUserArtists]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => {
      // Only update duration if metadata has already been loaded
      if (audio.readyState >= 1) { // HAVE_METADATA or higher
        const audioDuration = audio.duration;
        if (isFinite(audioDuration) && !isNaN(audioDuration) && audioDuration > 0) {
          setDuration(audioDuration);
        } else {
          setDuration(0);
        }
      }
    };
    
    const handleLoadedMetadata = () => {
      // Reset current time when new metadata loads
      setCurrentTime(0);
      audio.currentTime = 0;
      
      // Set duration from metadata
      const audioDuration = audio.duration;
      if (isFinite(audioDuration) && !isNaN(audioDuration) && audioDuration > 0) {
        setDuration(audioDuration);
      } else {
        setDuration(0);
      }
      
      // Clear any previous errors since metadata loaded successfully
      setError(null);
      
      console.log('Audio metadata loaded:', {
        duration: audioDuration,
        readyState: audio.readyState,
        networkState: audio.networkState
      });
    };
    const handleEnded = () => setIsPlaying(false);
    const handleError = (e: Event) => {
      console.error('Audio playback error:', e);
      setError('Failed to load audio file');
      setIsPlaying(false);
      setDuration(0);
      setCurrentTime(0);
    };
    const handleCanPlay = () => setError(null);
    const handlePlay = () => {
      setIsPlaying(true);
      if (track?.id) {
        analytics.trackPlay(track.id.toString(), 'give_feedback');
      }
    };
    const handlePause = () => {
      setIsPlaying(false);
      if (track?.id) {
        // Get current time directly from audio element to avoid dependency issues
        const currentAudioTime = audio.currentTime || 0;
        analytics.trackPause(track.id.toString(), currentAudioTime);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [track]);

  // Update audio volume when volume or mute state changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Set audio source when track changes
  useEffect(() => {
    if (track && audioRef.current) {
      // Reset metadata immediately when setting new track
      setCurrentTime(0);
      setDuration(0);
      setError(null);
      
      setTimeout(() => {
        if (track.id && audioRef.current) {
          // Primary approach: use API endpoint for audio streaming
          const streamUrl = trackApi.getAudioStreamUrl(track.id);
          console.log('Using stream URL:', streamUrl);
          audioRef.current.src = streamUrl;
          audioRef.current.load();
        } else if (track.audioUrl && audioRef.current) {
          // Fallback: try direct URL approach
          const fullAudioUrl = track.audioUrl.startsWith('http') 
            ? track.audioUrl 
            : `${API_BASE_URL}${track.audioUrl}`;
          console.log('Fallback: setting audio source to:', fullAudioUrl);
          audioRef.current.src = fullAudioUrl;
          audioRef.current.load();
        }
      }, 100);
    }
  }, [track]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || !track) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(err => {
        console.error('Failed to play audio:', err);
        setError('Failed to play audio file. The file may still be processing or there was a playback error.');
      });
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds: number): string => {
    // Handle invalid or non-finite values
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (isMuted && newVolume > 0) {
      setIsMuted(false);
    }
  };

  const handleMuteToggle = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    analytics.muteToggle(newMutedState);
  };

  const addTimestampedNote = () => {
    if (!currentNote.trim()) return;
    
    const newNote: CreateTimestampedNoteRequest = {
      timestampInSeconds: currentTime,
      note: currentNote.trim()
    };
    
    setTimestampedNotes(prev => [...prev, newNote]);
    setCurrentNote('');
  };

  const removeTimestampedNote = (index: number) => {
    setTimestampedNotes(prev => prev.filter((_, i) => i !== index));
  };

  const resetFeedbackForm = () => {
    setOverallRating(5);
    setCompositionRating(5);
    setMusicQualityRating(5);
    setProductionQualityRating(5);
    setReview('');
    setTimestampedNotes([]);
    setCurrentNote('');
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!track?.id || !user?.id) return;

    setSubmittingFeedback(true);
    setError(null);

    try {
      const feedbackData: CreateFeedbackRequest = {
        trackId: track.id,
        overallRating,
        compositionRating,
        musicQualityRating,
        productionQualityRating,
        review: review.trim() || undefined,
        timestampedNotes: timestampedNotes.length > 0 ? timestampedNotes : undefined
      };

      await feedbackApi.createFeedback(feedbackData);
      
      // Track feedback submission
      analytics.feedbackSubmit(overallRating, timestampedNotes.length > 0);
      
      setFeedbackSuccess('Feedback submitted successfully! Thank you for helping the community.');
      resetFeedbackForm();
      
      // Refresh track to get updated feedback counts
      loadTrack();
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleArtistModalOpen = async (artistId?: number) => {
    if (!artistId) return;
    
    setShowArtistModal(true);
    
    try {
      const artist = await artistApi.getArtistById(artistId);
      setFullArtist(artist);
    } catch (error) {
      console.error('Failed to fetch artist details:', error);
      // Keep the modal open but show basic info from track
      setFullArtist(track?.artist || null);
    }
  };

  const closeArtistModal = () => {
    setShowArtistModal(false);
    setFullArtist(null);
  };

  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <div className="give-feedback-tab">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  // Show track loading only for authenticated users
  if (loading && isAuthenticated) {
    return (
      <div className="give-feedback-tab">
        <div className="loading">Loading track...</div>
      </div>
    );
  }

  if (error && !track) {
    return (
      <div className="give-feedback-tab">
        <div className="error">
          <p>Error: {error}</p>
          <button className="retry-button" onClick={loadTrack}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Only show "Track not found" for authenticated users who tried to load a track
  if (!track && isAuthenticated && !loading) {
    return (
      <div className="give-feedback-tab">
        <div className="error">
          <p>Track not found</p>
        </div>
      </div>
    );
  }

  return (
    <FeedbackPageLayout>
      {/* Artist Setup Form */}
      {isAuthenticated && showArtistForm && (
        <FeedbackSection>
          <ArtistSetupCard
            artistName={artistName}
            setArtistName={setArtistName}
            artistUsesAI={artistUsesAI}
            setArtistUsesAI={setArtistUsesAI}
            onCreateArtist={createArtist}
            creatingArtist={creatingArtist}
            description="Before giving feedback, please create your artist profile:"
            showAsModal={false}
            headingText="Set Up Your Artist Profile"
            submitButtonText="Create Artist Profile"
          />
        </FeedbackSection>
      )}

      {/* Loading state for artist check */}
      {isAuthenticated && checkingArtists && (
        <FeedbackSection className="loading-section">
          <p>Checking your profile...</p>
        </FeedbackSection>
      )}

      {/* Track Information */}
      {track && isAuthenticated && !showArtistForm && !checkingArtists && (
        <FeedbackSection className="track-info-section">
          <TrackCard
            track={track}
            isSelected={true}
            showArtist={true}
            showFeedbackCount={true}
            showFeedbackSummary={false}
            showPlayButton={false}
            showShareButton={false}
            showDeleteButton={false}
            showReviewStatus={false}
            variant="info-only"
            onArtistClick={handleArtistModalOpen}
          />
        </FeedbackSection>
      )}

      {/* Audio Player */}
      {track && isAuthenticated && !showArtistForm && !checkingArtists && (track.audioUrl || track.id) && (
        <FeedbackSection>
          <TransportControl
            track={track}
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            volume={volume}
            isMuted={isMuted}
            onPlayPause={handlePlayPause}
            onSeek={handleSeek}
            onVolumeChange={handleVolumeChange}
            onMuteToggle={handleMuteToggle}
            onArtistClick={handleArtistModalOpen}
            audioRef={audioRef}
            variant="minimal"
          />
        </FeedbackSection>
      )}

      {/* Producer Notes */}
      {track && (track.producerNotes?.length ?? 0) > 0 && (
        <FeedbackSection className="producer-notes-section">
          <h3>Producer Notes</h3>
          <p>{track.producerNotes}</p>
        </FeedbackSection>
      )}

      {/* Success Message */}
      {feedbackSuccess && (
        <FeedbackMessage type="success">
          {feedbackSuccess}
        </FeedbackMessage>
      )}

      {/* Error Message */}
      {error && (
        <FeedbackMessage type="error">
          {error}
        </FeedbackMessage>
      )}

      {/* Login Required Message */}
      {!isAuthenticated && !authLoading && (
        <FeedbackSection className="login-required-message">
          <h3>Login Required</h3>
          <p>Please log in or create an account to access this track and provide feedback to help artists improve their music.</p>
          <button 
            className="login-prompt-button"
            onClick={() => setShowAuthModal(true)}
          >
            Log In to Continue
          </button>
        </FeedbackSection>
      )}

      {/* Feedback Form */}
      {track && isAuthenticated && !showArtistForm && !checkingArtists && (
        <FeedbackSection>
          <FeedbackForm
            trackTitle={track.title}
            overallRating={overallRating}
            setOverallRating={setOverallRating}
            compositionRating={compositionRating}
            setCompositionRating={setCompositionRating}
            musicQualityRating={musicQualityRating}
            setMusicQualityRating={setMusicQualityRating}
            productionQualityRating={productionQualityRating}
            setProductionQualityRating={setProductionQualityRating}
            review={review}
            setReview={setReview}
            timestampedNotes={timestampedNotes}
            currentNote={currentNote}
            setCurrentNote={setCurrentNote}
            addTimestampedNote={addTimestampedNote}
            removeTimestampedNote={removeTimestampedNote}
            currentTime={currentTime}
            formatTime={formatTime}
            onSubmit={handleFeedbackSubmit}
            onCancel={resetFeedbackForm}
            submittingFeedback={submittingFeedback}
            cancelButtonText="Reset Form"
          />
        </FeedbackSection>
      )}

      {/* Authentication Modal */}
      <FeedbackModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        title="Login Required"
        description="Please log in or create an account to provide feedback on this track."
      >
        {authView === 'login' && (
          <LoginForm 
            onSwitchToRegister={() => setAuthView('register')} 
            onSwitchToForgotPassword={() => setAuthView('forgot-password')}
          />
        )}
        {authView === 'register' && (
          <RegisterForm onSwitchToLogin={() => setAuthView('login')} />
        )}
        {authView === 'forgot-password' && (
          <ForgotPasswordForm onSwitchToLogin={() => setAuthView('login')} />
        )}
      </FeedbackModal>

      {/* Artist Overview Modal */}
      {showArtistModal && (fullArtist || track?.artist) && (
        <ArtistOverview 
          artist={fullArtist || track!.artist!}
          showAsModal={true}
          onClose={closeArtistModal}
        />
      )}
    </FeedbackPageLayout>
  );
};

export default RequestFeedback;